import React, { useMemo, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';

type DevRunResponse = {
  success: boolean;
  code?: number;
  stdout?: string;
  stderr?: string;
  report?: any;
  result?: any;
  metrics?: any;
  artifacts?: Record<string, string | string[] | null>;
};

const ARTIFACTS_ROOT = '/api/dev/edgedocs/tests/artifacts/file?path=';
const SAMPLE_FIXTURE = '/home/edgelexllc/projects/edgelexapp/webelieve-frontend/src/features/dms/EdgeDocs/EdgeDocsCore/devtools/docx_import_fixture.json';

function JsonBlock({ data }: { data: unknown }) {
  if (data == null) return null;
  return (
    <pre style={{ background: '#0b1020', color: '#e3f2fd', padding: 12, borderRadius: 6, overflowX: 'auto', fontSize: 12 }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function ArtifactLink({ label, path }: { label: string; path?: string | null }) {
  const { keycloak } = useKeycloak();
  if (!path) return null;
  const onClick = async () => {
    try {
      try { await (keycloak as any)?.updateToken?.(30); } catch {}
      const token = (keycloak as any)?.token as string | undefined;
      const url = `${ARTIFACTS_ROOT}${encodeURIComponent(path)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        alert(`Failed to open artifact (${res.status}): ${text || res.statusText}`);
        return;
      }
      const contentType = res.headers.get('Content-Type') || '';
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      // Try to open in a new tab for images/JSON/PDF, otherwise download
      if (contentType.includes('image') || contentType.includes('pdf') || contentType.includes('json') || contentType.includes('text')) {
        window.open(objectUrl, '_blank', 'noopener');
      } else {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = label.replace(/\s+/g, '_');
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke later
      setTimeout(() => URL.revokeObjectURL(objectUrl), 15000);
    } catch (e: any) {
      alert(`Error opening artifact: ${e?.message || 'unknown error'}`);
    }
  };
  return (
    <button onClick={onClick} style={{ marginRight: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff' }}>
      {label}
    </button>
  );
}

function usePost<T = DevRunResponse>() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);
  const post = async (url: string, body: any, extraHeaders?: Record<string, string>) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(extraHeaders || {}) },
        credentials: 'include',
        body: JSON.stringify(body || {}),
      });
      const json = (await res.json()) as T;
      if (!res.ok) throw new Error((json as any)?.error || `HTTP ${res.status}`);
      setData(json);
      return json;
    } catch (e: any) {
      setError(e?.message || 'Request failed');
      throw e;
    } finally {
      setLoading(false);
    }
  };
  return { loading, error, data, post };
}

function Section({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid #e3e8ef', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
        <div>{right}</div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
      <div style={{ width: 180, color: '#334155' }}>{label}</div>
      <input style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function CheckboxField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#334155' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Button({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ padding: '8px 12px', borderRadius: 6, background: disabled ? '#94a3b8' : '#1d4ed8', color: 'white', border: 'none' }}>{children}</button>
  );
}

function VisualDiffPanel() {
  const { keycloak } = useKeycloak();
  const [baseline, setBaseline] = useState('');
  const [candidate, setCandidate] = useState('');
  const [outDir, setOutDir] = useState('');
  const [config, setConfig] = useState('/home/edgelexllc/projects/edgelexapp/webelieve-frontend/src/features/dms/EdgeDocs/EdgeDocsCore/tests/.visualdiff.json');
  const [baselineContext, setBaselineContext] = useState('');
  const [candidateContext, setCandidateContext] = useState('');
  const [updateGoldens, setUpdateGoldens] = useState(false);
  const { loading, error, data, post } = usePost<DevRunResponse>();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const diffImages = useMemo(() => (Array.isArray(data?.artifacts?.diffImages) ? (data?.artifacts?.diffImages as string[]) : []), [data]);
  const resolvedImages = diffImages.map((p) => `${ARTIFACTS_ROOT}${encodeURIComponent(p)}`);

  const baselineRaster = (page: number) => data?.artifacts?.testDir ? `${ARTIFACTS_ROOT}${encodeURIComponent(`${data.artifacts.testDir as string}/work/baseline/page_${String(page).padStart(3, '0')}.png`)}` : '';
  const candidateRaster = (page: number) => data?.artifacts?.testDir ? `${ARTIFACTS_ROOT}${encodeURIComponent(`${data.artifacts.testDir as string}/work/candidate/page_${String(page).padStart(3, '0')}.png`)}` : '';

  async function run() {
    try { await keycloak?.updateToken?.(30); } catch {}
    const token = keycloak?.token;
    const auth = token ? { Authorization: `Bearer ${token}` } : undefined;
    await post('/api/dev/edgedocs/tests/visual-diff', {
      baseline, candidate, outDir: outDir || undefined, config, baselineContext: baselineContext || undefined, candidateContext: candidateContext || undefined, updateGoldens,
    }, auth);
  }

  function fillDemoVisualOnly() {
    setBaseline('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-samples/baseline.pdf');
    setCandidate('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-samples/candidate.pdf');
    setOutDir('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-diff');
    setBaselineContext('');
    setCandidateContext('');
  }

  function fillDemoWithSemantic() {
    setBaseline('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-samples/baseline.pdf');
    setCandidate('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-samples/candidate.pdf');
    setOutDir('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-diff');
    setBaselineContext('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-samples/baseline_context.json');
    setCandidateContext('/home/edgelexllc/projects/edgelexapp/.artifacts/edgedocs-samples/candidate_context.json');
  }

  return (
    <Section title="PDF Visual + Semantic Diff" right={<Button onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run'}</Button>}>
      <div style={{ marginBottom: 8, color: '#475569' }}>Enter absolute server paths. Use a demo preset to auto‑fill sample files.</div>
      <Field label="Baseline PDF" value={baseline} onChange={setBaseline} placeholder="/abs/path/baseline.pdf" />
      <Field label="Candidate PDF" value={candidate} onChange={setCandidate} placeholder="/abs/path/candidate.pdf" />
      <Field label="Output Dir (optional)" value={outDir} onChange={setOutDir} placeholder="/abs/path/out" />
      <Field label="Config JSON" value={config} onChange={setConfig} />
      <Field label="Baseline Context (optional)" value={baselineContext} onChange={setBaselineContext} placeholder="/abs/path/baseline_context.json" />
      <Field label="Candidate Context (optional)" value={candidateContext} onChange={setCandidateContext} placeholder="/abs/path/candidate_context.json" />
      <CheckboxField label="Update Goldens" checked={updateGoldens} onChange={setUpdateGoldens} />
      <div style={{ display: 'flex', gap: 8, margin: '8px 0 12px' }}>
        <Button onClick={fillDemoVisualOnly}>Use Visual‑only Demo</Button>
        <Button onClick={fillDemoWithSemantic}>Use Visual+Semantic Demo</Button>
      </div>
      {error && <div style={{ color: '#dc2626', marginTop: 8 }}>{error}</div>}
      {data && (
        <div style={{ marginTop: 12 }}>
          {/* Summary banner */}
          <VisualSummary report={data.report} success={data.success} code={data.code} />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {resolvedImages.map((src, idx) => (
              <img key={src} src={src} alt={`diff-${idx + 1}`} style={{ width: 160, height: 'auto', cursor: 'pointer', border: '1px solid #e2e8f0' }} onClick={() => setSelectedImage(String(idx + 1))} />
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <ArtifactLink label="report.json" path={(data.artifacts?.reportPath as string) || ''} />
            <ArtifactLink label="outDir" path={(data.artifacts?.testDir as string) || ''} />
          </div>
          <JsonBlock data={data.report} />
        </div>
      )}

      {/* Simple overlay viewer (attempts baseline/candidate rasters for the selected page) */}
      {selectedImage && (
        <OverlayViewer
          page={Number(selectedImage)}
          baselineSrc={baselineRaster(Number(selectedImage))}
          candidateSrc={candidateRaster(Number(selectedImage))}
          diffSrc={resolvedImages[Number(selectedImage) - 1]}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </Section>
  );
}

function VisualSummary({ report, success, code }: { report: any; success?: boolean; code?: number }) {
  if (!report) {
    return (
      <div style={{ marginBottom: 8 }}>
        <strong>Result:</strong> {success ? 'PASS' : 'FAIL'} {typeof code === 'number' ? `(code ${code})` : ''}
      </div>
    );
  }
  const totalPct = typeof report.totalMismatchPct === 'number' ? (report.totalMismatchPct * 100).toFixed(3) + '%' : '—';
  const pages = Array.isArray(report.perPage) ? report.perPage.length : 0;
  const violations = report.violations?.totalViolation ? 'Yes' : 'No';
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 12 }}>
      <div style={{ fontWeight: 600, color: success ? '#065f46' : '#991b1b' }}>{success ? 'PASS' : 'FAIL'}</div>
      <div>Pages: <strong>{pages}</strong></div>
      <div>Total mismatch: <strong>{totalPct}</strong></div>
      <div>Any violation: <strong>{violations}</strong></div>
      {typeof code === 'number' && <div>Exit code: <strong>{code}</strong></div>}
    </div>
  );
}

function OverlayViewer({ page, baselineSrc, candidateSrc, diffSrc, onClose }: { page: number; baselineSrc?: string; candidateSrc?: string; diffSrc?: string; onClose: () => void }) {
  const [mode, setMode] = useState<'diff' | 'ab'>('diff');
  const [split, setSplit] = useState(50);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 12, width: '90%', height: '90%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <strong>Page {page}</strong>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={mode} onChange={(e) => setMode(e.target.value as any)}>
              <option value="diff">Diff Overlay</option>
              <option value="ab">A/B Scrub</option>
            </select>
            {mode === 'ab' && (
              <input type="range" min={0} max={100} value={split} onChange={(e) => setSplit(Number(e.target.value))} />
            )}
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
        <div style={{ flex: 1, position: 'relative', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          {mode === 'diff' ? (
            <img src={diffSrc} alt="diff" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <img src={baselineSrc} alt="baseline" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
              <div style={{ position: 'absolute', inset: 0, width: `${split}%`, overflow: 'hidden' }}>
                <img src={candidateSrc} alt="candidate" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SemanticQAPanel() {
  const { keycloak } = useKeycloak();
  const [sourceAst, setSourceAst] = useState('');
  const [exportedDocx, setExportedDocx] = useState('');
  const [outDir, setOutDir] = useState('');
  const [config, setConfig] = useState('/home/edgelexllc/projects/edgelexapp/webelieve-frontend/src/features/dms/EdgeDocs/EdgeDocsCore/tests/.semanticqa.json');
  const { loading, error, data, post } = usePost<DevRunResponse>();
  async function run() {
    try { await keycloak?.updateToken?.(30); } catch {}
    const token = keycloak?.token;
    const auth = token ? { Authorization: `Bearer ${token}` } : undefined;
    await post('/api/dev/edgedocs/tests/docx/semantic-qa', { sourceAst, exportedDocx, outDir: outDir || undefined, config }, auth);
  }
  return (
    <Section title="DOCX Semantic QA" right={<Button onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run'}</Button>}>
      <div style={{ marginBottom: 8, color: '#475569' }}>Provide the AST JSON that was exported and the DOCX file to evaluate. Use Round‑Trip first if you need a DOCX to test.</div>
      <Field label="Source AST JSON" value={sourceAst} onChange={setSourceAst} placeholder="/abs/path/source_ast.json" />
      <Field label="Exported DOCX" value={exportedDocx} onChange={setExportedDocx} placeholder="/abs/path/exported.docx" />
      <Field label="Output Dir (optional)" value={outDir} onChange={setOutDir} placeholder="/abs/path/out" />
      <Field label="Config JSON" value={config} onChange={setConfig} />
      {error && <div style={{ color: '#dc2626', marginTop: 8 }}>{error}</div>}
      {data && (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>{data.success ? 'PASS' : 'FAIL'} {typeof data.code === 'number' ? `(code ${data.code})` : ''}</div>
          <ArtifactLink label="semantic_qa_report.json" path={(data.artifacts?.reportPath as string) || ''} />
          <JsonBlock data={data.report || data.result} />
        </div>
      )}
    </Section>
  );
}

function RoundTripPanel() {
  const { keycloak } = useKeycloak();
  const [inputJson, setInputJson] = useState('');
  const [outDir, setOutDir] = useState('');
  const { loading, error, data, post } = usePost<DevRunResponse>();
  async function run() {
    try { await keycloak?.updateToken?.(30); } catch {}
    const token = keycloak?.token;
    const auth = token ? { Authorization: `Bearer ${token}` } : undefined;
    await post('/api/dev/edgedocs/tests/docx/roundtrip', { inputJson, outDir: outDir || undefined }, auth);
  }
  return (
    <Section title="DOCX Round‑Trip" right={<Button onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run'}</Button>}>
      <div style={{ marginBottom: 8, color: '#475569' }}>Enter a python‑docx JSON input, or click “Use sample fixture”, then Run to produce a DOCX and AST.</div>
      <Field label="Input JSON (python‑docx)" value={inputJson} onChange={setInputJson} placeholder="/abs/path/docx.json" />
      <Field label="Output Dir (optional)" value={outDir} onChange={setOutDir} placeholder="/abs/path/out" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Button onClick={() => setInputJson(SAMPLE_FIXTURE)}>Use sample fixture</Button>
      </div>
      {error && <div style={{ color: '#dc2626', marginTop: 8 }}>{error}</div>}
      {data && (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Result:</strong> {data.success ? 'PASS' : 'FAIL'} {typeof data.code === 'number' ? `(code ${data.code})` : ''}
          </div>
          <ArtifactLink label="roundtrip.docx" path={(data.artifacts?.roundtripDocx as string) || ''} />
          <ArtifactLink label="ast.json" path={(data.artifacts?.astJson as string) || ''} />
          <JsonBlock data={data.metrics || data.report || data.result} />
        </div>
      )}
    </Section>
  );
}

function ImportFixtureRunnerPanel() {
  const { keycloak } = useKeycloak();
  const [inputJson, setInputJson] = useState(SAMPLE_FIXTURE);
  const { loading, error, data, post } = usePost<DevRunResponse>();
  async function run() {
    try { await keycloak?.updateToken?.(30); } catch {}
    const token = keycloak?.token;
    const auth = token ? { Authorization: `Bearer ${token}` } : undefined;
    await post('/api/dev/edgedocs/tests/docx/import-fixture', { inputJson }, auth);
  }
  return (
    <Section title="DOCX Import Fixture Runner" right={<Button onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run'}</Button>}>
      <div style={{ marginBottom: 8, color: '#475569' }}>Quickly map the sample or a custom fixture from JSON → AST and view metrics.</div>
      <Field label="Input JSON" value={inputJson} onChange={setInputJson} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Button onClick={() => setInputJson(SAMPLE_FIXTURE)}>Use sample fixture</Button>
      </div>
      {error && <div style={{ color: '#dc2626', marginTop: 8 }}>{error}</div>}
      {data && (
        <div style={{ marginTop: 12 }}>
          <JsonBlock data={data.metrics || data.result || data.report} />
        </div>
      )}
    </Section>
  );
}

function ImportValidatorPanel() {
  const { keycloak } = useKeycloak();
  const [fixtureDir, setFixtureDir] = useState('/home/edgelexllc/projects/edgelexapp/webelieve-frontend/src/features/dms/EdgeDocs/EdgeDocsCore/devtools/fixtures/sample_case');
  const [updateGoldens, setUpdateGoldens] = useState(false);
  const { loading, error, data, post } = usePost<DevRunResponse>();
  async function run() {
    try { await keycloak?.updateToken?.(30); } catch {}
    const token = keycloak?.token;
    const auth = token ? { Authorization: `Bearer ${token}` } : undefined;
    await post('/api/dev/edgedocs/tests/docx/import-validator', { fixtureDir, updateGoldens }, auth);
  }
  return (
    <Section title="DOCX Import Validator (Goldens)" right={<Button onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run'}</Button>}>
      <div style={{ marginBottom: 8, color: '#475569' }}>Compares current import metrics to goldens in the fixture directory. Enable “Update goldens” to overwrite.</div>
      <Field label="Fixture Directory" value={fixtureDir} onChange={setFixtureDir} />
      <CheckboxField label="Update goldens" checked={updateGoldens} onChange={setUpdateGoldens} />
      {error && <div style={{ color: '#dc2626', marginTop: 8 }}>{error}</div>}
      {data && (
        <div style={{ marginTop: 12 }}>
          <JsonBlock data={data.result || data.report} />
        </div>
      )}
    </Section>
  );
}

function TablesRichExportPanel() {
  const { keycloak } = useKeycloak();
  const [inputJson, setInputJson] = useState(SAMPLE_FIXTURE);
  const [outFile, setOutFile] = useState('');
  const { loading, error, data, post } = usePost<DevRunResponse>();
  async function run() {
    try { await keycloak?.updateToken?.(30); } catch {}
    const token = keycloak?.token;
    const auth = token ? { Authorization: `Bearer ${token}` } : undefined;
    await post('/api/dev/edgedocs/tests/docx/tables-rich', { inputJson, outFile: outFile || undefined }, auth);
  }
  return (
    <Section title="DOCX TablesRich Export" right={<Button onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run'}</Button>}>
      <div style={{ marginBottom: 8, color: '#475569' }}>Exports tables with rich cell content provided by the resolver. Use the sample fixture to demo.</div>
      <Field label="Input JSON" value={inputJson} onChange={setInputJson} />
      <Field label="Out File (optional)" value={outFile} onChange={setOutFile} placeholder="/abs/path/tablesRich_export.docx" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Button onClick={() => setInputJson(SAMPLE_FIXTURE)}>Use sample fixture</Button>
      </div>
      {error && <div style={{ color: '#dc2626', marginTop: 8 }}>{error}</div>}
      {data && (
        <div style={{ marginTop: 12 }}>
          <ArtifactLink label="tablesRich_export.docx" path={(data.artifacts?.outFile as string) || ''} />
          <JsonBlock data={data.result || data.report} />
        </div>
      )}
    </Section>
  );
}

export default function EdgeDocsTestLab() {
  const [tab, setTab] = useState<'visual' | 'qa' | 'rt' | 'fixture' | 'validator' | 'tables'>('visual');
  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0 }}>EdgeDocs Test Lab</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <TabButton active={tab === 'visual'} onClick={() => setTab('visual')}>Visual Diff</TabButton>
        <TabButton active={tab === 'qa'} onClick={() => setTab('qa')}>Semantic QA</TabButton>
        <TabButton active={tab === 'rt'} onClick={() => setTab('rt')}>Round‑Trip</TabButton>
        <TabButton active={tab === 'fixture'} onClick={() => setTab('fixture')}>Import Fixture</TabButton>
        <TabButton active={tab === 'validator'} onClick={() => setTab('validator')}>Import Validator</TabButton>
        <TabButton active={tab === 'tables'} onClick={() => setTab('tables')}>TablesRich Export</TabButton>
      </div>

      {tab === 'visual' && <VisualDiffPanel />}
      {tab === 'qa' && <SemanticQAPanel />}
      {tab === 'rt' && <RoundTripPanel />}
      {tab === 'fixture' && <ImportFixtureRunnerPanel />}
      {tab === 'validator' && <ImportValidatorPanel />}
      {tab === 'tables' && <TablesRichExportPanel />}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid', borderColor: active ? '#1d4ed8' : '#cbd5e1', background: active ? '#1d4ed8' : '#fff', color: active ? '#fff' : '#0f172a' }}>{children}</button>
  );
}


